import React, { useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import {
    FirstDataRenderedEvent,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    PaginationChangedEvent,
    RowDataUpdatedEvent,
} from 'ag-grid-community';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import {
    BudgetProposalT,
    BudgetProposalWithResourcesT,
} from 'routes/BudgetModule/config/types';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    checkIfShouldChangePage,
    getManageBudgetColDefs,
    getPageNumberFromUrl,
    updateGridPageIfNecessary,
} from 'routes/Admin/Budget/BudgetManagement/BudgetList/helpers';
import CanResourceEditCellRenderer from './CellRenderers/CanResourceEditCellRenderer/CanResourceEditCellRenderer';
import { useGetVentilationOptions } from './CellRenderers/ProjectionVentilationCellRenderer/hooks/hooks';
import ModalOptionsContext from './CellRenderers/optionsContext';
import { useGetResourceOptions } from './CellRenderers/CanResourceEditCellRenderer/hooks';
import PhaseEditorCellRenderer from './CellRenderers/PhaseEditorCellRenderers/PhaseEditorCellRenderer';
import PluriannualProjectionVentilationsCellRenderer from './CellRenderers/ProjectionVentilationCellRenderer/PluriannualProjectionsVentilationCellRenderer';
import ProjectionVentilationsCellRenderer from './CellRenderers/ProjectionVentilationCellRenderer/ProjectionsVentilationsCellRenderer';
import { useIntegrationSoftwares } from 'helpers/integrations';

type Props = {
    data: BudgetProposalWithResourcesT[];
    isLoading: boolean;
};

const StyTableContainer = styled('div', {
    name: 'StyTableContainer',
})({
    width: '100%',
    height: '100%',
    '& .ag-cell': {
        display: 'flex',
        justifyContent: 'center',
    },
});

const components = {
    phaseOverviewCellRenderer: PhaseEditorCellRenderer,
    actionCellRenderer: CanResourceEditCellRenderer,
    textFloatingFilter: TextFloatingFilter,
    projectionVentilationCellRenderer: ProjectionVentilationsCellRenderer,
    pluriannualProjectionVentilationCellRenderer:
        PluriannualProjectionVentilationsCellRenderer,
};

export default function ManageBudgetTable({
    data,
    isLoading,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { data: ventilationOptions, isLoading: isVentilationLoading } =
        useGetVentilationOptions();

    const { data: resourceOptions, isLoading: isResourceLoading } =
        useGetResourceOptions();

    const { data: softwares } = useIntegrationSoftwares();

    const memoizedContextValue = useMemo(
        () => ({
            ventilationOptions,
            resourceOptions,
        }),
        [ventilationOptions, resourceOptions],
    );
    const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
    const [isReady, setIsReady] = React.useState(false);

    React.useEffect(() => {
        if (!gridApi) {
            return;
        }
        if (
            isLoading ||
            isVentilationLoading ||
            isResourceLoading ||
            softwares === undefined
        ) {
            gridApi.setGridOption('loading', true);
        } else {
            if (!isReady) {
                setIsReady(true);
            }
            gridApi.setGridOption('loading', false);
            gridApi.refreshCells({ force: true });
            gridApi.autoSizeAllColumns();
        }
    }, [
        isLoading,
        gridApi,
        isReady,
        isVentilationLoading,
        isResourceLoading,
        softwares,
    ]);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        setGridApi(event.api);
    }, []);

    const onFirstDataRendered = useCallback(
        (params: FirstDataRenderedEvent) => {
            params.api.autoSizeAllColumns();
        },
        [],
    );

    const getRowId = useCallback(
        (params: GetRowIdParams<BudgetProposalT>) => params.data.id.toString(),
        [],
    );

    /**
     * Set initial url state on first render.
     */
    React.useEffect(() => {
        const isPageInUrl = new URLSearchParams(location.search).has('page');

        if (isPageInUrl) return;
        navigate(
            {
                search: '?page=1',
            },
            { replace: true },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only on first render
    }, []);

    /**
     * Update the grid page when the url page changes (when user clicks on back/forward button on his browser)
     */
    React.useEffect(() => {
        if (!gridApi) return;
        updateGridPageIfNecessary(location.search, gridApi);
    }, [location.search, gridApi]);

    /**
     * Trigger the pagination change if the URL contains a page query param, and the page is different from the current one.
     * This callback is passed to onRowDataUpdated event. It prevents coming back to page 1 when the user interacts with the grids, e.g when he changes the phase.
     */
    const stayOnSameGridPageOnDataChange = useCallback(
        (params: RowDataUpdatedEvent) => {
            updateGridPageIfNecessary(location.search, params.api);
        },
        [location.search],
    );

    /**
     * Updates the URL Search params with the current page.
     *
     * This callback is passed to onPaginationChanged event. It is currently called:
     * - each time the user clicks on a pagination button
     * - when we programmatically change the page
     * - because of re-renderings somehow.
     *
     * Only the first case is a legit reason to update the URL.
     */
    const syncPaginationWithUrl = useCallback(
        (params: PaginationChangedEvent) => {
            const urlSearchParams = new URLSearchParams(location.search);
            const urlPageNumber = getPageNumberFromUrl(location.search);
            // means that the user clicked on the pagination button
            const isNewPageEvent = params.newPage;
            if (!isNewPageEvent) {
                return;
            }
            const shouldChangePage = checkIfShouldChangePage(
                urlPageNumber,
                params.api,
            );
            if (!shouldChangePage) {
                return;
            }
            const newPage = params.api.paginationGetCurrentPage() + 1;
            urlSearchParams.set('page', newPage.toString());
            navigate({
                search: urlSearchParams.toString(),
            });
        },
        [navigate, location.search],
    );
    const colDefs = useMemo(() => getManageBudgetColDefs(t), [t]);
    const gridContext = useMemo(
        () => ({
            softwares,
        }),
        [softwares],
    );
    return (
        <StyTableContainer className="ag-theme-material">
            <ModalOptionsContext.Provider value={memoizedContextValue}>
                <AgGridReact
                    columnDefs={colDefs}
                    context={gridContext}
                    headerHeight={40}
                    rowHeight={115}
                    rowData={data}
                    getRowId={
                        getRowId // needed for transactions
                    }
                    components={components}
                    pagination
                    paginationAutoPageSize
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                    onRowDataUpdated={stayOnSameGridPageOnDataChange}
                    onPaginationChanged={syncPaginationWithUrl}
                    suppressColumnVirtualisation // seems to improve e2e test flakyness
                />
            </ModalOptionsContext.Provider>
        </StyTableContainer>
    );
}
